import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as LogoXXS } from '@repo/common/assets/logos/koready/xxs/logo-light.svg';
import {
  applyTypography,
  BREAKPOINTS,
  useMediaQuery,
  BaseButton,
  useAuth,
} from '@repo/common';

const GNB_HEIGHT_CLASS = 'h-11 tabletH:h-16 desktopL:h-14';
const ADMIN_ROLES = ['A', 'ROLE_A', 'ADMIN', 'ROLE_ADMIN'];
// 백엔드 role 값은 E/EMPLOYER 계열 그대로, 노출 라벨만 'for Business'
const BUSINESS_ROLES = [
  'E',
  'ROLE_E',
  'EMPLOYER',
  'ROLE_EMPLOYER',
  'EMPLOYEE',
  'ROLE_EMPLOYEE',
];

const MinimalGnb = ({
  className = '',
  type = 'basic',
  leftButtonLabel = 'Save draft',
  rightButtonLabel = 'Next',
  onLeftButtonClick,
  onRightButtonClick,
  hideActions = false,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isTabletH = useMediaQuery(`(min-width: ${BREAKPOINTS.tabletH}px)`);
  const buttonSize = isTabletH ? 'm' : 's';

  const normalizedRole = String(user?.role ?? '').toUpperCase();
  const isAdmin = ADMIN_ROLES.includes(normalizedRole);
  const isBusiness = BUSINESS_ROLES.includes(normalizedRole);

  const roleLabel = isAdmin ? 'Admin' : '';
  const leftGapClass = isBusiness
    ? 'gap-[6px] tabletH:gap-2'
    : roleLabel
      ? 'gap-1 tabletH:gap-2'
      : 'gap-0';

  return (
    <>
      <div className={GNB_HEIGHT_CLASS} />

      <div
        className={`fixed top-0 left-0 w-full z-[10] ${GNB_HEIGHT_CLASS} flex flex-row items-center gap-3 tabletH:gap-4 desktopL:gap-6 overflow-hidden px-4 tabletH:px-6 desktopL:px-[80px] py-[6px] tabletH:py-3 desktopL:py-2 ${
          type === 'basic'
            ? 'bg-grayscale-gray01'
            : 'bg-grayscale-gray00 border-grayscale-gray02 border-b'
        } ${className}`}
      >
        <div
          className={`flex items-center ${leftGapClass} py-1 tabletH:py-2 shrink-0`}
        >
          <BaseButton
            type='text'
            size='s'
            onClick={() => navigate('/')}
            className='h-6 w-[82px] min-w-0 shrink-0 overflow-hidden border-0 bg-transparent p-0 text-grayscale-gray09 hover:bg-transparent hover:text-grayscale-gray09'
            aria-label='Go to home'
            icon={<LogoXXS className='h-6 w-[75px] shrink-0' />}
          />

          {roleLabel && (
            <span className='typo-b14Strong tabletH:typo-b16Strong text-text-primary !font-medium'>
              {roleLabel}
            </span>
          )}

          {isBusiness &&
            (isTabletH ? (
              <span className='inline-flex items-center gap-1 desktopL:gap-[6px]'>
                <span
                  className={`text-text-primary !font-medium whitespace-nowrap ${applyTypography(
                    'b16Strong',
                    'for Business',
                  )}`}
                >
                  for Business
                </span>
                <span
                  className={`inline-flex shrink-0 items-center justify-center rounded-[99px] bg-purple-purple01 px-1 py-[2px] desktopL:h-6 desktopL:px-[6px] desktopL:py-1 text-purple-purple05 !font-medium ${applyTypography(
                    'b12Strong',
                    'Beta',
                  )}`}
                >
                  Beta
                </span>
              </span>
            ) : (
              <span className='inline-flex items-center gap-[2px] leading-[1.4]'>
                <span className='typo-b13Strong text-text-primary !font-medium'>
                  Business
                </span>
                <span className='typo-b11 text-text-secondary'>(Beta)</span>
              </span>
            ))}
        </div>

        {!hideActions ? (
          <div className='flex items-center gap-2 ml-auto'>
            <BaseButton
              type='text'
              size={buttonSize}
              hideIcon
              rounded='pill'
              textclass='!font-medium tracking-[0]'
              className='!rounded-[99px] shrink-0'
              onClick={onLeftButtonClick}
            >
              {leftButtonLabel}
            </BaseButton>

            <BaseButton
              hideIcon
              size={buttonSize}
              rounded='pill'
              textclass='!font-medium tracking-[0]'
              className='!rounded-[99px] shrink-0 min-w-[80px] tabletH:min-w-[100px] desktopL:min-w-[80px]'
              onClick={onRightButtonClick}
            >
              {rightButtonLabel}
            </BaseButton>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default MinimalGnb;
